<template>
  <div v-if="permission || superUser" :value="$route.path">
    <v-card
      v-if="subscription.unpaid_billings && subscription.unpaid_billings.length > 0"
      class="pa-3"
    >
      <v-card>
        <v-card-title class="text-h5 font-weight-bold">{{
          translations.unpaidBillings[lang]
        }}</v-card-title>
        <BillingsTable :billings="subscription.unpaid_billings" is-expandable />
      </v-card>
    </v-card>

    <v-card class="pa-3 my-3 pb-5">
      <v-row class="d-flex justify-center">
        <v-col cols="12" md="12" sm="12">
          <v-card elevation="0">
            <v-card-title class="text-h5 font-weight-bold justify-start pa-0 d-flex">
              <span class="text-center pa-1">{{ translations.currentPlanDetails[lang] }}</span>
            </v-card-title>
          </v-card>
        </v-col>
        <v-col cols="12" xl="12" md="12">
          <v-card elevation="3" style="height: 100%">
            <v-simple-table class="plan-details-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th
                      class="text-left"
                      v-for="(header, i) in headersPlanDetails"
                      :key="i"
                      style="min-width: 10%"
                    >
                      {{ header[lang] }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="align-start">
                      <span v-if="subscriptionPlan.plan_name">{{
                        subscriptionPlan.plan_name[lang] || subscriptionPlan.plan_name.en
                      }}</span>
                      <v-tooltip
                        color="secondary"
                        bottom
                        v-if="subscriptionPlan && subscriptionPlan.plan_description"
                      >
                        <template v-slot:activator="{ on }">
                          <v-btn icon v-on="on" @click="helpDialog = true">
                            <v-icon v-text="icons.mdiInformationOutline"></v-icon>
                          </v-btn>
                        </template>

                        <span class="font-weight-bold" style="white-space: pre">{{
                          subscriptionPlan.plan_description[lang]
                        }}</span>
                      </v-tooltip>
                      <v-tooltip v-if="superUser" color="secondary" top>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-if="superUser"
                            class="text-capitalize"
                            depressed
                            fab
                            x-small
                            v-on="on"
                            @click="
                              () => {
                                changePlanDialog = true;
                              }
                            "
                          >
                            <v-icon size="20" v-text="icons.mdiSwapVertical"></v-icon>
                          </v-btn>
                        </template>
                        <span
                          class="font-weight-bold"
                          v-text="translations.planChange[lang]"
                        ></span>
                      </v-tooltip>
                    </td>
                    <td>
                      <div class="py-2" v-if="subscriptionPlan.attributes">
                        <div
                          v-for="(attr, key) in subscriptionPlan.attributes"
                          :key="key"
                          class="d-flex align-start pb-1"
                        >
                          <div class="font-weight-bold mr-2 text-capitalize">
                            {{ key.replace('_', ' ') }}:
                          </div>
                          <div class="d-flex flex-wrap align-center">
                            <span
                              v-for="(attValue, attKey) in attr"
                              :key="key + attKey"
                              class="d-flex align-center mr-3 pb-1"
                            >
                              <span v-if="attKey == 'cost'">{{ attValue }}€</span>
                              <span
                                v-else-if="typeof attValue === 'boolean'"
                                class="d-flex align-center"
                              >
                                <span class="text-capitalize ml-1">{{
                                  attKey.replace('_', ' ')
                                }}</span
                                >:
                                <v-icon :color="attValue ? 'success' : 'error'" small>
                                  {{ attValue ? icons.mdiCheckCircleOutline : icons.mdiClose }}
                                </v-icon>
                              </span>
                              <span v-else class="text-capitalize">
                                {{ attKey.replace('_', ' ') }}: {{ attValue }}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div v-else>
                        <span class="text-h5"> - </span>
                      </div>
                    </td>
                    <td class="d-flex align-center">
                      <span>{{ subscriptionPlan.plan_price }} €</span>
                    </td>
                  </tr>
                  <tr></tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col v-if="subscription.custom_attributes && subscription.custom_attributes.length">
          <span class="text-h6 font-weight-bold mb-2">
            {{ translations.customAttributes[lang] }}
          </span>
          <v-list dense>
            <v-list-item
              v-for="(attribute, i) in subscription.custom_attributes"
              :key="i"
              :style="{ borderTop: i && false ? `1px solid #00000055` : 'none' }"
            >
              <v-list-item-content class="py-0">
                <v-col class="d-flex flex-row align-items-center py-0"
                  ><v-icon color="black" large>mdi-circle-small</v-icon>
                  <v-col class="d-flex align-items-center px-0">
                    <span>{{ attribute[lang] }}</span>
                  </v-col>
                </v-col>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-card>

    <v-card class="pa-3 my-3 pb-5">
      <v-row class="d-flex justify-center">
        <v-col cols="12" md="12" sm="12">
          <v-card elevation="0">
            <v-card-title class="text-h5 font-weight-bold justify-start pa-0 d-flex">
              <span class="text-center pa-1">{{ translations.currentBillingSum[lang] }}</span>
              <v-tooltip v-if="currentBilling && superUser" color="secondary" top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    color="secondary darken-3"
                    icon
                    v-on="on"
                    :loading="loading"
                    @click="openRecalculateModal(currentBilling)"
                  >
                    <v-icon v-text="icons.mdiReceiptTextEditOutline"></v-icon>
                  </v-btn>
                </template>
                <span class="font-weight-bold" v-text="translations.calculateBilling[lang]"></span>
              </v-tooltip>
            </v-card-title>
          </v-card>
        </v-col>
        <v-col cols="12" xl="12" md="12">
          <v-card elevation="3">
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th
                      class="text-left"
                      v-for="(header, i) in headersNextBilling"
                      :key="i"
                      style="min-width: 10%"
                    >
                      {{ header[lang] }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div v-if="currentBilling">
                        <span>{{ getPeriodString(currentBilling) }}</span>
                        <v-tooltip color="secondary" top v-if="superUser">
                          <template v-slot:activator="{ on }">
                            <v-btn class="mr-4" icon v-on="on" @click="openDateDialog()">
                              <v-icon v-text="icons.mdiCalendarEdit"></v-icon>
                            </v-btn>
                          </template>

                          <span
                            class="font-weight-bold"
                            v-text="translations.changePeriod[lang]"
                          ></span>
                        </v-tooltip>
                      </div>
                      <div v-else>
                        <span class="font-weight-regular text-h4"> &infin; </span>
                      </div>
                    </td>
                    <td>
                      <span>
                        <span v-if="currentBilling">{{ currentBilling.total }}</span>
                        <span v-else>0</span>€
                      </span>
                    </td>
                  </tr>
                  <tr></tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col v-if="currentBilling" cols="12" xl="12" md="12">
          <v-card elevation="0">
            <v-card-title class="text-h6 font-weight-bold justify-center pr-0 pl-0">
              <span
                class="text-center pa-1"
                style="background-color: #2a3042; color: white; width: 100%"
                >{{ translations.paymentInfo[lang] }}</span
              >
            </v-card-title>

            <v-card elevation="6">
              <v-row class="d-flex justify-start pb-3 px-0 mx-0 pt-0" v-if="superUser">
                <v-toolbar flat style="border-bottom: 1px solid #00000033">
                  <v-btn
                    color="secondary"
                    class="text-capitalize"
                    depressed
                    @click="openInvoiceItemDialog(null)"
                  >
                    {{ translations.addInvoiceItem[lang] }}
                  </v-btn>
                </v-toolbar>
              </v-row>
              <InvoiceTable
                :invoice="currentBilling.invoice"
                :billing="currentBilling"
                :allow-invoice-item-actions="superUser"
                @edit-invoice-item="openInvoiceItemDialog"
                @delete-invoice-item="openDeleteInvoiceItem"
              />
            </v-card>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog v-model="dateDialog" max-width="500">
      <b-card
        v-if="superUser"
        :title="translations.dateDialogTitle[lang]"
        @cancel="dateDialog = false"
        @submit="changeBillingPeriod"
        :loading="loading"
      >
        <v-col cols="12" md="12" class="d-flex flex-column pa-0">
          <span class="text-subtitle-1">{{ translations.chooseDate[lang] }}:</span>
          <v-col class="d-flex align-center">
            <v-menu v-model="fromDateMenu" :close-on-content-click="false" max-width="290">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="localizeDate(changeBillingPeriodData.payment_due)"
                  clearable
                  outlined
                  dense
                  :label="translations.date[lang]"
                  hide-details="auto"
                  readonly
                  prepend-inner-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="changeBillingPeriodData.payment_due = ''"
                ></v-text-field>
              </template>
              <v-date-picker
                dark
                v-model="changeBillingPeriodData.payment_due"
                :locale="locale"
                @change="fromDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col class="d-flex align-center py-0">
            <v-checkbox
              color="secondary"
              class="mt-3 pt-0"
              hide-details
              v-model="changeBillingPeriodData.cancel_previous_billing"
            >
              <template v-slot:label>
                <h4 class="subtitle-2">
                  {{ translations.cancelPreviousBilling[lang] }}
                </h4>
              </template>
            </v-checkbox>
          </v-col>
        </v-col>
      </b-card>
    </v-dialog>

    <v-dialog v-model="changePlanDialog" max-width="600">
      <b-card
        v-if="superUser"
        :title="translations.planChange[lang]"
        :loading="loading"
        :error-message="''"
        :submit-text="translations.submit"
        @cancel="changePlanDialog = false"
        @submit="changePlan(newPlanId)"
      >
        <div class="subtitle-1 font-weight-medium pl-2">
          {{ translations.newSubscriptionPlan[lang] }}:
        </div>
        <v-row no-gutters class="mt-3 mb-1">
          <v-col cols="12">
            <v-autocomplete
              :items="subscriptionPlans"
              :item-text="`plan_name[${lang}]`"
              item-value="store_subscription_plan_id"
              :label="`${translations.selectA[lang]}${translations.subscriptionPlan[lang]}`"
              v-model="newPlanId"
              menu-props="offsetY"
              hide-details
              outlined
              dense
              no-filter
              clearable
              :search-input.sync="searchPlans"
              :loading="loading"
              :disable-lookup="true"
            >
              <template v-slot:item="{ item }">
                <v-col class="d-flex flex-column py-2 px-0" style="max-width: 550px">
                  <v-col class="py-0 d-flex justify-space-between"
                    ><span class="font-weight-bold">{{ item.plan_name[lang] }}</span>
                    <div v-if="item.store_name" class="pt-1">
                      <v-chip label class="mt-n2">
                        <span> {{ item.store_name }} </span>
                      </v-chip>
                    </div></v-col
                  >
                  <v-col class="ml-4 py-0 pl-2 plan-select-description"
                    ><span class="font-italic">{{ item.plan_description[lang] }}</span></v-col
                  >
                </v-col>
              </template>
              <template v-slot:append-item>
                <div v-intersect="endIntersectPlans"></div>
                <v-list-item v-if="loading">
                  <v-list-item-action class="ml-1">
                    <v-row class="d-flex align-center">
                      <v-btn
                        class="text-capitalize"
                        depressed
                        fab
                        small
                        :loading="true"
                        style="background: transparent"
                      >
                      </v-btn>
                      <span class="pa-2">{{ translations.loadMore[lang] }}</span>
                    </v-row>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-col class="d-flex align-center py-2">
          <v-checkbox
            v-model="changePlanImmediateBilling"
            color="secondary"
            class="mt-3 pt-0"
            hide-details
          >
            <template v-slot:label>
              <h4 class="subtitle-2">
                {{ translations.immediateBilling[lang] }}
              </h4>
            </template>
          </v-checkbox>
        </v-col>
      </b-card>
    </v-dialog>

    <v-dialog v-model="invoiceItemDialog.show" max-width="600">
      <b-card
        :title="
          invoiceItemDialog.mode === 'create'
            ? translations.addInvoiceItem[lang]
            : translations.editInvoiceItem[lang]
        "
        :loading="invoiceItemDialog.loading"
        :disabled="!formValid || invoiceItemDialog.loading"
        @cancel="invoiceItemDialog.show = false"
        @submit="submitInvoiceItem"
      >
        <v-col cols="12" class="py-0 mb-4">
          <b-text-field
            v-model="invoiceItemDialog.form.attribute"
            :label="
              translations.attributeLabel && translations.attributeLabel[lang]
                ? translations.attributeLabel[lang]
                : 'Attribute'
            "
            outlined
            dense
            :success="success.attribute"
            :error-messages="error.attribute"
            @focus="error.attribute = ''"
            @input="touched.attribute = true"
            @blur="validateAttribute"
            :readonly="invoiceItemDialog.mode === 'edit'"
          >
            <template v-slot:append>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" small icon color="warning"> ! </v-btn>
                </template>
                <span>{{ translations.attributeTooltip[lang] }}</span>
              </v-tooltip>
            </template>
          </b-text-field>
        </v-col>
        <v-col cols="12">
          <b-text-field
            v-model="invoiceItemDialog.form.title[invoiceItemDialog.titleLang]"
            :label="translations.serviceTitle[lang]"
            outlined
            dense
            :success="invoiceItemDialog.titleLang === 'el' ? success.title : false"
            :error-messages="error.title"
            @focus="error.title = ''"
            @input="touched.title = true"
            @blur="validateTitle"
          >
            <template v-slot:append>
              <b-lang-menu v-model="invoiceItemDialog.titleLang" type="inner"></b-lang-menu>
            </template>
          </b-text-field>

          <v-row
            v-if="invoiceItemDialog.mode === 'create'"
            class="d-flex align-center pa-0"
            no-gutters
          >
          </v-row>

          <b-text-field
            v-model="invoiceItemDialog.form.unit_value"
            :label="translations.headerUnitValue[lang]"
            type="number"
            outlined
            dense
            :success="success.unit_value"
            :error-messages="error.unit_value"
            @focus="error.unit_value = ''"
            @input="touched.unit_value = true"
            @blur="validateUnitValue"
          />
          <b-text-field
            v-model="invoiceItemDialog.form.quantity"
            :label="translations.headerQuantity[lang]"
            type="number"
            outlined
            dense
            :success="success.quantity"
            :error-messages="error.quantity"
            @focus="error.quantity = ''"
            @input="touched.quantity = true"
            @blur="validateQuantity"
          />
        </v-col>
      </b-card>
    </v-dialog>

    <v-dialog v-model="deleteInvoiceItemModal.show" max-width="500">
      <b-card
        v-if="deleteInvoiceItemModal.item"
        type="delete"
        :title="translations.delete[lang]"
        :loading="deleteInvoiceItemModal.loading"
        @cancel="deleteInvoiceItemModal.show = false"
        @submit="confirmDeleteInvoiceItem"
      >
        <div class="subtitle-1 font-weight-medium pl-2">
          {{ translations.deleteInvoiceItemConfirm[lang] }}
          <span class="font-weight-bold text--primary">
            {{
              deleteInvoiceItemModal.item.title && deleteInvoiceItemModal.item.title[lang]
                ? deleteInvoiceItemModal.item.title[lang]
                : deleteInvoiceItemModal.item.title
            }}
          </span>
        </div>
      </b-card>
    </v-dialog>

    <v-dialog v-model="recalculateModal.show" max-width="500">
      <b-card
        v-if="recalculateModal.show && recalculateModal.billing"
        :title="translations.recalculateBilling[lang]"
        :loading="recalculateModal.loading"
        @cancel="closeRecalculateModal"
        @submit="recalculateCurrentBilling"
      >
        <div class="subtitle-1 font-weight-medium pl-2">
          {{ translations.sureRecalculateBilling[lang] }}
          <span class="font-weight-bold text--primary">
            {{ getPeriodString(recalculateModal.billing) }}
          </span>
          {{ translations.and[lang] }}
          <span class="font-weight-bold text--primary">
            €{{ recalculateModal.billing.total || '' }}
          </span>
          {{ translations.total[lang].toLowerCase() }}
          {{ translations.questionmark[lang] }}
        </div>
      </b-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiCheckCircleOutline,
  mdiMinusCircleOutline,
  mdiClose,
  mdiCheckboxBlankCircle,
  mdiCheckboxMarkedOutline,
  mdiInformationOutline,
  mdiCalendarEdit,
  mdiSwapVertical,
  mdiReceiptTextEditOutline,
} from '@mdi/js';
import { mapState, mapMutations, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
import upperFirst from 'lodash/upperFirst';
import { getBillingPeriodDisplay } from '@/utils/helpers';
import translations from '@/utils/translations/general/settings/billingDashboard';
import validators from '@/utils/validators/general/settings/billingDashboard/invoiceItem';
import InvoiceTable from '@/components/general/settings/billingDashboard/InvoiceTable.vue';
import BillingsTable from '@/components/general/settings/billingDashboard/BillingsTable.vue';

export default {
  name: 'BillingDashboardCurrent',

  mixins: [validators, translations],
  components: { InvoiceTable, BillingsTable },

  data() {
    return {
      icons: {
        mdiCheckCircleOutline,
        mdiMinusCircleOutline,
        mdiClose,
        mdiCheckboxBlankCircle,
        mdiCheckboxMarkedOutline,
        mdiInformationOutline,
        mdiCalendarEdit,
        mdiSwapVertical,
        mdiReceiptTextEditOutline,
      },
      invoiceItemDialog: {
        show: false,
        mode: 'create',
        loading: false,
        titleLang: this.$route.params.lang,
        form: {
          title: { el: '', en: '', it: '' },
          attribute: '',
          unit_value: null,
          quantity: 1,
        },
        originalItem: null,
      },
      changeTitleElAuto: true,
      changeTitleEnAuto: true,
      changeTitleItAuto: true,
      deleteInvoiceItemModal: {
        show: false,
        loading: false,
        item: null,
      },
      dateDialog: false,
      newPaymentDue: '',
      fromDateMenu: false,
      changePlanDialog: false,
      changePlanImmediateBilling: false,
      newPlanId: null,
      searchPlans: '',
      pagePlans: 1,
      changeBillingPeriodData: {
        payment_due: '',
        cancel_previous_billing: true,
      },
      recalculateModal: {
        show: false,
        loading: false,
        billing: null,
      },
    };
  },

  computed: {
    ...mapState([
      'loading',
      'errorMessage',
      'serverItemsLength',
      'superUser',
      'panelType',
      'store',
    ]),
    ...mapState('general/settings/billingDashboard/current', [
      'subscription',
      'subscriptionPlan',
      'currentBilling',
      'subscriptionPlans',
      'subscriptionPlansPagination',
    ]),
    ...mapState('general/settings/billingDashboard/paymentMethods', ['selectedMethod']),

    lang() {
      return this.$route.params.lang;
    },
    locale() {
      if (this.lang == 'el') return 'el-GR';
      if (this.lang == 'it') return 'it-IT';
      return 'en-GB';
    },
    dateLang() {
      let lang = 'en-US';
      if (this.lang === 'el') {
        lang = 'el-GR';
      } else if (this.lang === 'it') {
        lang = 'it-IT';
      }
      return lang;
    },

    headersPlanDetails() {
      return [this.translations.name, this.translations.attributes, this.translations.price];
    },
    headersNextBilling() {
      return [this.translations.period, this.translations.totalEstimated];
    },
    services() {
      let services = [];
      let invoice = this.currentBilling?.invoice || {
        sms: {
          unit_value: 0.04,
          quantity: 123,
          unit_discount: 0,
        },
        company_stores: {
          unit_value: 20,
          quantity: 1,
          unit_discount: 0,
        },
      };

      for (const [key, value] of Object.entries(invoice)) {
        value.name = upperFirst(key.replace('_', ' '));
        value.total =
          (Number(value.unit_value || 0) - Number(value.unit_discount || 0)) *
          Number(value.quantity || 0);
        services.push(value);
      }

      return services;
    },

    permission() {
      return (
        this.superUser ||
        (this.$store.state.permissions.general_settings
          ? this.$store.state.permissions.general_settings.billing_dashboard.read
          : null)
      );
    },

    queryPlans() {
      return this.searchPlans
        ? `?q=${this.searchPlans}&page=${this.pagePlans}`
        : `?page=${this.pagePlans}`;
    },
  },
  methods: {
    ...mapMutations(['setNotification']),
    ...mapActions('general/settings/billingDashboard/current', [
      'updatePaymentDue',
      'getSubscriptionPlans',
      'changeSubscriptionPlan',
      'getCurrent',
      'createInvoiceItem',
      'updateInvoiceItem',
      'deleteInvoiceItem',
    ]),

    ...mapActions('general/settings/billingDashboard', [
      'fetchBillingDashboardData',
      'calculateBilling',
    ]),

    ...mapActions('general/settings/billingDashboard/paymentMethods', ['getPaymentMethods']),
    openDateDialog() {
      this.dateDialog = true;
    },
    localizeDate(date) {
      return date
        ? new Date(date).toLocaleString(this.locale, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : '';
    },

    async changeBillingPeriod() {
      await this.updatePaymentDue(this.changeBillingPeriodData);
      this.dateDialog = false;
    },

    async changePlan(id) {
      let res = await this.changeSubscriptionPlan({
        store_subscription_plan_id: id,
        immediate_billing: !!this.changePlanImmediateBilling,
      });
      if (res.code == 200) {
        await this.fetchBillingDashboardData();
        await this.getPaymentMethods();
        this.$router.push(
          `/${this.lang}/${this.panelType}/general-settings/billing-dashboard/payment-methods`
        );
      }
      if (res.error) {
        this.setNotification({
          show: true,
          type: 'error',
          text: res.message,
        });
      }
      this.newPlanId = null;
      this.changePlanDialog = false;
      this.changePlanImmediateBilling = false;
    },

    async loadMorePlans() {
      this.pagePlans++;
      this.handleSearchPlans();
    },

    handleSearchPlans() {
      const body = {
        query: this.queryPlans,
        page: this.pagePlans,
      };
      this.getSubscriptionPlans(body);
    },

    endIntersectPlans(entries, observer, isIntersecting) {
      if (
        isIntersecting &&
        this.subscriptionPlans?.length < this.subscriptionPlansPagination?.total
      ) {
        this.loadMorePlans();
      }
    },
    async recalculateCurrentBilling() {
      try {
        if (this.currentBilling?.billing_id) {
          this.recalculateModal.loading = true;
          const res = await this.calculateBilling(this.currentBilling.billing_id);

          if (res && !res.error) {
            this.setNotification({
              show: true,
              type: 'success',
              text: this.translations.billingUpdated[this.lang],
            });
          } else {
            this.setNotification({
              show: true,
              type: 'error',
              text: res?.message || 'Error calculating current billing',
            });
          }
        }
      } catch (error) {
        this.recalculateModal.loading = false;
        console.log('error:', error.data);
        this.setNotification({
          show: true,
          type: 'error',
          text: error.response?.data?.message || 'Error calculating current billing',
        });
      } finally {
        this.recalculateModal.loading = false;
        this.closeRecalculateModal();
      }
    },
    openRecalculateModal(billing) {
      this.recalculateModal.billing = billing;
      this.recalculateModal.show = true;
    },
    closeRecalculateModal() {
      this.recalculateModal.show = false;
      this.recalculateModal.billing = null;
    },
    openInvoiceItemDialog(service) {
      if (service) {
        this.invoiceItemDialog.mode = 'edit';
        this.invoiceItemDialog.originalItem = service;
        if (service.title && typeof service.title === 'object') {
          this.invoiceItemDialog.form.title = {
            el: service.title.el || '',
            en: service.title.en || '',
            it: service.title.it || '',
          };
        } else {
          this.invoiceItemDialog.form.title = { el: '', en: '', it: '' };
        }
        this.invoiceItemDialog.titleLang = this.lang;
        this.invoiceItemDialog.form.attribute = service.attribute || '';
        this.invoiceItemDialog.form.unit_value = service.unit_value;
        this.invoiceItemDialog.form.quantity = service.quantity;
      } else {
        this.invoiceItemDialog.mode = 'create';
        this.invoiceItemDialog.originalItem = null;
        this.invoiceItemDialog.form = {
          title: { el: '', en: '', it: '' },
          attribute: ``,
          unit_value: null,
          quantity: 1,
        };
        this.invoiceItemDialog.titleLang = this.lang;
      }
      if (this.touched) {
        this.touched.title = false;
        this.touched.attribute = false;
        this.touched.unit_value = false;
        this.touched.quantity = false;
      }
      this.invoiceItemDialog.show = true;
    },

    async submitInvoiceItem() {
      this.validateTitle();
      this.validateAttribute();
      this.validateUnitValue();
      this.validateQuantity();
      if (!this.formValid) {
        this.setNotification({
          show: true,
          type: 'error',
          text:
            this.error.title ||
            this.error.attribute ||
            this.error.unit_value ||
            this.error.quantity ||
            'Please fix form errors',
        });
        return;
      }

      try {
        this.invoiceItemDialog.loading = true;
        const form = this.invoiceItemDialog.form;
        const titleObj = {
          el: form.title?.el || '',
          en: form.title?.en || '',
          it: form.title?.it || '',
        };

        if (this.invoiceItemDialog.mode === 'create') {
          const payload = {
            title: titleObj,
            quantity: Number(form.quantity) || 1,
            unit_value: Number(form.unit_value) || 0,
            attribute: form.attribute || `custom_${Date.now()}`,
            is_custom: true,
          };
          const res = await this.createInvoiceItem(payload);
          if (!res || res.error) {
            this.setNotification({
              show: true,
              type: 'error',
              text: res?.message || 'Error creating invoice item',
            });
          } else {
            this.setNotification({
              show: true,
              type: 'success',
              text: this.translations.invoiceItemAdded[this.lang],
            });
            this.invoiceItemDialog.show = false;
          }
        } else {
          const id = this.invoiceItemDialog.originalItem.invoice_item_id;
          const payload = {
            title: titleObj,
            quantity: Number(form.quantity) || 1,
            unit_value: Number(form.unit_value) || 0,
          };
          const res = await this.updateInvoiceItem({ id, body: payload });
          if (!res || res.error) {
            this.setNotification({
              show: true,
              type: 'error',
              text: res?.message || 'Error updating invoice item',
            });
          } else {
            this.setNotification({
              show: true,
              type: 'success',
              text: this.translations.invoiceItemUpdated[this.lang],
            });
            this.invoiceItemDialog.show = false;
          }
        }
      } catch (error) {
        console.error(error);
        this.setNotification({
          show: true,
          type: 'error',
          text: error?.response?.data?.message || 'Error',
        });
      } finally {
        this.invoiceItemDialog.loading = false;
      }
    },

    openDeleteInvoiceItem(service) {
      this.deleteInvoiceItemModal.item = service;
      this.deleteInvoiceItemModal.show = true;
    },

    async confirmDeleteInvoiceItem() {
      try {
        this.deleteInvoiceItemModal.loading = true;
        const res = await this.deleteInvoiceItem(this.deleteInvoiceItemModal.item.invoice_item_id);
        if (!res || res.error) {
          this.setNotification({
            show: true,
            type: 'error',
            text: res?.message || 'Error deleting invoice item',
          });
        } else {
          this.setNotification({
            show: true,
            type: 'success',
            text: this.translations.invoiceItemDeleted[this.lang],
          });
        }
      } catch (error) {
        console.error(error);
        this.setNotification({
          show: true,
          type: 'error',
          text: error?.response?.data?.message || 'Error deleting invoice item',
        });
      } finally {
        this.deleteInvoiceItemModal.loading = false;
        this.deleteInvoiceItemModal.show = false;
      }
    },
    getPeriodString(item) {
      return getBillingPeriodDisplay(item, this.dateLang);
    },
  },

  watch: {
    currentBilling: {
      immediate: true,
      handler(val) {
        if (val && typeof val.invoice == 'object') {
          if (val.period.from) {
            this.changeBillingPeriodData.payment_due = val.period.from;
          }
          if (val.invoice?.length === 0) {
            val.invoice = null;
          }
        }
      },
    },

    permission: {
      // immediate: true,
      handler(val) {
        if (!val && !this.superUser) {
          this.$router.replace(`/${this.lang}/${this.panelType}/forbidden-gateway`);
        }
      },
    },
    searchPlans: {
      handler(val) {
        this.$nextTick(() => {
          let newPlan = this.subscriptionPlans?.find(
            (x) => x.store_subscription_plan_id == this.newPlanId
          );
          if (!newPlan || newPlan.plan_name[this.lang] !== val) {
            this.pagePlans = 1;
            this.debouncedSearchPlans();
          }
        });
      },
    },
    'invoiceItemDialog.form.title.en': function (val) {
      if (this.lang == 'en') {
        if (this.changeTitleElAuto) this.invoiceItemDialog.form.title.el = val;
        if (this.changeTitleItAuto) this.invoiceItemDialog.form.title.it = val;
        this.changeTitleEnAuto = false;
      } else if (val != this.invoiceItemDialog.form.title[this.lang]) {
        this.changeTitleEnAuto = false;
      }
    },
    'invoiceItemDialog.form.title.el': function (val) {
      if (this.lang == 'el') {
        if (this.changeTitleEnAuto) this.invoiceItemDialog.form.title.en = val;
        if (this.changeTitleItAuto) this.invoiceItemDialog.form.title.it = val;
        this.changeTitleElAuto = false;
      } else if (val != this.invoiceItemDialog.form.title[this.lang]) {
        this.changeTitleElAuto = false;
      }
    },
    'invoiceItemDialog.form.title.it': function (val) {
      if (this.lang == 'it') {
        if (this.changeTitleElAuto) this.invoiceItemDialog.form.title.el = val;
        if (this.changeTitleEnAuto) this.invoiceItemDialog.form.title.en = val;
        this.changeTitleItAuto = false;
      } else if (val != this.invoiceItemDialog.form.title[this.lang]) {
        this.changeTitleItAuto = false;
      }
    },
    'invoiceItemDialog.titleLang'(val) {
      if (val !== this.lang) {
        if (val === 'el') this.changeTitleElAuto = false;
        if (val === 'en') this.changeTitleEnAuto = false;
        if (val === 'it') this.changeTitleItAuto = false;
      }
    },
    changePlanDialog(val) {
      if (!val) {
        this.changePlanImmediateBilling = false;
      }
    },
  },

  created() {
    this.debouncedSearchPlans = debounce(this.handleSearchPlans, 500);
  },
};
</script>
<style>
.grey-overlay {
  position: absolute;
  top: -148px;
  right: -22px;
  width: 100rem;
  height: 120vh;
  background-color: #5e5e5ecc;
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dashed {
  border: 1px dashed grey;
  border-radius: 10px;
}

.relative {
  position: relative;
}

.theme--light.v-tabs > .v-tabs-bar {
  width: 100%;
}

.v-tabs-bar.v-item-group > * {
  width: 100%;
}

.v-slide-group__content {
  width: 100%;
}

.my-subscription {
  background-color: #2a3042;
  color: white !important;
  border-radius: 5px 5px 0px 0px;
}

.plan-details-table tr {
  height: 100%;
}

.plan-details-table td {
  height: 100% !important;
  vertical-align: stretch !important;
}
.plan-select-description {
  border-left: 1px solid black;
}
</style>
